import React, { useState } from 'react';
import { PlusIcon, FileUpIcon, TruckIcon, FileTextIcon, AlertCircleIcon, HandshakeIcon, CalendarDaysIcon } from 'lucide-react';
import { ActionCard } from '../../dashboard/ActionCard';
import { TabNavigation } from '../../dashboard/TabNavigation';
import { InProgressJobs } from '../contractor/InProgressJobs';
import { CompletedJobs } from '../contractor/CompletedJobs';
import { ImportJobsModal } from '../contractor/ImportJobsModal';
import { CreateJobForm } from '../contractor/CreateJobForm';
import { AssignJobsToTruckingCompanies } from './AssignJobsToTruckingCompanies';
import { ContractorJobRequests } from './ContractorJobRequests';
import { AvailableTrucks } from './AvailableTrucks';
import { BrokerInProgressJobs } from './BrokerInProgressJobs';
import { SelfGeneratedJobs } from './SelfGeneratedJobs';
interface TruckingCompany {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  rating: number;
  truckCount: number;
  isDefault?: boolean;
  rates?: {
    [key: string]: number;
  };
}
interface AvailableTruck {
  id: string;
  truckNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  status: 'available' | 'assigned' | 'maintenance';
  truckingCompanyId: string;
  truckingCompanyName: string;
  assignedDriver?: string;
  driverName?: string;
  driverPhone?: string;
  hourlyRate: number;
  availability: {
    [date: string]: boolean;
  };
  currentLocation?: string;
  lastUpdated: string;
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  material: string;
  numberOfTrucks: string;
  loadLocation: string;
  dumpLocation: string;
  foreman?: string;
  startTime?: string;
  truckType?: string;
  customer?: string;
  source?: 'broker' | 'contractor';
  contractorId?: string;
  contractorName?: string;
  assignments?: {
    truckingCompanyId: string;
    truckCount: number;
  }[];
  status?: string;
}
interface BrokerDashboardProps {
  onNavigate?: (page: string) => void;
}
export const BrokerDashboard = ({
  onNavigate
}: BrokerDashboardProps) => {
  const [activeTab, setActiveTab] = useState('jobs');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateJobForm, setShowCreateJobForm] = useState(false);
  const [pendingJobs, setPendingJobs] = useState<Job[]>([]);
  const [prefilledJobData, setPrefilledJobData] = useState<any>(null);
  // Helper function to get date strings for the next N days
  function getDateString(daysFromNow: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  }
  // Sample available trucks from trucking companies (simulated based on trucking company availability assignments)
  const [availableTrucks] = useState<AvailableTruck[]>([
  // Mountain View Trucking trucks
  {
    id: 'mvt-t001',
    truckNumber: 'T-001',
    type: 'dump truck 12',
    make: 'Peterbilt',
    model: '567',
    year: 2020,
    status: 'available',
    truckingCompanyId: 'trucking1',
    truckingCompanyName: 'Mountain View Trucking',
    assignedDriver: 'Mike Johnson',
    driverName: 'Mike Johnson',
    driverPhone: '555-123-4567',
    hourlyRate: 90.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: false,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: false
    },
    currentLocation: 'Salt Lake City, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'mvt-t004',
    truckNumber: 'T-004',
    type: 'dump truck 10',
    make: 'Freightliner',
    model: '114SD',
    year: 2018,
    status: 'available',
    truckingCompanyId: 'trucking1',
    truckingCompanyName: 'Mountain View Trucking',
    assignedDriver: 'Robert Williams',
    driverName: 'Robert Williams',
    driverPhone: '555-456-7890',
    hourlyRate: 85.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: false,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    },
    currentLocation: 'West Valley City, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'mvt-t005',
    truckNumber: 'T-005',
    type: 'dump truck 14+',
    make: 'Peterbilt',
    model: '348',
    year: 2022,
    status: 'available',
    truckingCompanyId: 'trucking1',
    truckingCompanyName: 'Mountain View Trucking',
    assignedDriver: 'James Anderson',
    driverName: 'James Anderson',
    driverPhone: '555-567-8901',
    hourlyRate: 95.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: false,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: false
    },
    currentLocation: 'Murray, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'mvt-t010',
    truckNumber: 'T-010',
    type: 'belly dump',
    make: 'Peterbilt',
    model: '367',
    year: 2018,
    status: 'available',
    truckingCompanyId: 'trucking1',
    truckingCompanyName: 'Mountain View Trucking',
    assignedDriver: 'Joshua Moore',
    driverName: 'Joshua Moore',
    driverPhone: '555-012-3456',
    hourlyRate: 130.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: false,
      [getDateString(6)]: true,
      [getDateString(7)]: false
    },
    currentLocation: 'Sandy, UT',
    lastUpdated: new Date().toISOString()
  },
  // Desert Haul Express trucks
  {
    id: 'dhe-t007',
    truckNumber: 'DH-007',
    type: 'strong arm 16+',
    make: 'Volvo',
    model: 'VHD64F200',
    year: 2019,
    status: 'available',
    truckingCompanyId: 'trucking2',
    truckingCompanyName: 'Desert Haul Express',
    assignedDriver: 'Christopher Davis',
    driverName: 'Christopher Davis',
    driverPhone: '555-789-0123',
    hourlyRate: 105.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: true
    },
    currentLocation: 'Provo, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'dhe-t008',
    truckNumber: 'DH-008',
    type: 'super side 30tn+',
    make: 'Kenworth',
    model: 'T470',
    year: 2021,
    status: 'available',
    truckingCompanyId: 'trucking2',
    truckingCompanyName: 'Desert Haul Express',
    assignedDriver: 'Matthew Miller',
    driverName: 'Matthew Miller',
    driverPhone: '555-890-1234',
    hourlyRate: 115.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: false,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    },
    currentLocation: 'Ogden, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'dhe-t012',
    truckNumber: 'DH-012',
    type: 'dump truck 12',
    make: 'Freightliner',
    model: 'M2 106',
    year: 2021,
    status: 'available',
    truckingCompanyId: 'trucking2',
    truckingCompanyName: 'Desert Haul Express',
    assignedDriver: 'Kenneth Jackson',
    driverName: 'Kenneth Jackson',
    driverPhone: '555-234-5678',
    hourlyRate: 85.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: false,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: true
    },
    currentLocation: 'Layton, UT',
    lastUpdated: new Date().toISOString()
  },
  // Dirt Logics Trucking trucks
  {
    id: 'dlt-t006',
    truckNumber: 'DL-006',
    type: 'strong arm 12',
    make: 'International',
    model: 'HX620',
    year: 2020,
    status: 'available',
    truckingCompanyId: 'dirt-logics-trucking',
    truckingCompanyName: 'Dirt Logics Trucking',
    assignedDriver: 'Michael Brown',
    driverName: 'Michael Brown',
    driverPhone: '555-678-9012',
    hourlyRate: 103.0,
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: false,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    },
    currentLocation: 'Draper, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'dlt-t013',
    truckNumber: 'DL-013',
    type: 'side dump',
    make: 'Volvo',
    model: 'VHD84F200',
    year: 2022,
    status: 'available',
    truckingCompanyId: 'dirt-logics-trucking',
    truckingCompanyName: 'Dirt Logics Trucking',
    assignedDriver: 'Paul White',
    driverName: 'Paul White',
    driverPhone: '555-345-6789',
    hourlyRate: 118.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: false,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    },
    currentLocation: 'Midvale, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'dlt-t015',
    truckNumber: 'DL-015',
    type: 'strong arm 14',
    make: 'Mack',
    model: 'Anthem',
    year: 2020,
    status: 'available',
    truckingCompanyId: 'dirt-logics-trucking',
    truckingCompanyName: 'Dirt Logics Trucking',
    assignedDriver: 'Donald Martin',
    driverName: 'Donald Martin',
    driverPhone: '555-567-8901',
    hourlyRate: 108.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: false
    },
    currentLocation: 'South Jordan, UT',
    lastUpdated: new Date().toISOString()
  }, {
    id: 'dlt-t018',
    truckNumber: 'DL-018',
    type: 'belly dump',
    make: 'Freightliner',
    model: 'Cascadia',
    year: 2021,
    status: 'available',
    truckingCompanyId: 'dirt-logics-trucking',
    truckingCompanyName: 'Dirt Logics Trucking',
    assignedDriver: 'Brian Martinez',
    driverName: 'Brian Martinez',
    driverPhone: '555-890-1234',
    hourlyRate: 133.0,
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: true
    },
    currentLocation: 'Herriman, UT',
    lastUpdated: new Date().toISOString()
  }]);
  // Sample trucking companies data
  const truckingCompanies: TruckingCompany[] = [{
    id: 'trucking1',
    name: 'Mountain View Trucking',
    contactPerson: 'Bob Wilson',
    email: 'bob@mountainviewtrucking.com',
    phone: '555-234-5678',
    rating: 4.7,
    truckCount: 15,
    isDefault: true,
    rates: {
      'dump truck 10': 85.0,
      'dump truck 12': 90.0,
      'dump truck 14+': 95.0,
      'strong arm 12': 100.0,
      'strong arm 14': 105.0,
      'strong arm 16+': 110.0,
      'side dump': 115.0,
      'super side 30tn+': 120.0,
      'jumbo side 35tn+': 125.0,
      'belly dump': 130.0,
      'double belly': 135.0
    }
  }, {
    id: 'trucking2',
    name: 'Desert Haul Express',
    contactPerson: 'Maria Garcia',
    email: 'maria@deserthaulexpress.com',
    phone: '555-345-6789',
    rating: 4.5,
    truckCount: 12,
    rates: {
      'dump truck 10': 80.0,
      'dump truck 12': 85.0,
      'dump truck 14+': 90.0,
      'strong arm 12': 95.0,
      'strong arm 14': 100.0,
      'strong arm 16+': 105.0,
      'side dump': 110.0,
      'super side 30tn+': 115.0,
      'jumbo side 35tn+': 120.0,
      'belly dump': 125.0,
      'double belly': 130.0
    }
  }, {
    id: 'dirt-logics-trucking',
    name: 'Dirt Logics Trucking',
    contactPerson: 'Dirt Logics Trucking',
    email: 'trucking@dirtlogics.com',
    phone: '555-456-7890',
    rating: 4.9,
    truckCount: 18,
    rates: {
      'dump truck 10': 88.0,
      'dump truck 12': 93.0,
      'dump truck 14+': 98.0,
      'strong arm 12': 103.0,
      'strong arm 14': 108.0,
      'strong arm 16+': 113.0,
      'side dump': 118.0,
      'super side 30tn+': 123.0,
      'jumbo side 35tn+': 128.0,
      'belly dump': 133.0,
      'double belly': 138.0
    }
  }];
  // Sample contractor job requests (jobs assigned by contractors to this broker)
  const contractorJobRequests = [{
    id: 'contractor-job-1',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    date: '2024-01-15',
    material: 'Gravel',
    numberOfTrucks: '3 trucks',
    truckType: 'dump truck 12',
    loadLocation: 'West Valley Materials Yard',
    dumpLocation: 'Downtown SLC Project',
    foreman: 'John Smith',
    foremanPhone: '555-123-4567',
    startTime: '07:00',
    contractorName: 'ABC Construction',
    contractorId: 'contractor1',
    requestedDate: '2024-01-15',
    urgency: 'high',
    estimatedHours: 8,
    comments: 'Priority job - materials needed early morning',
    status: 'pending'
  }, {
    id: 'contractor-job-2',
    jobName: 'Highway 89 Expansion Project',
    jobNumber: 'HWY-2024-002',
    date: '2024-01-16',
    material: 'Sand',
    numberOfTrucks: '2 trucks',
    truckType: 'strong arm 14',
    loadLocation: 'Sand Pit North',
    dumpLocation: 'Highway 89 Site',
    foreman: 'Mike Johnson',
    foremanPhone: '555-234-5678',
    startTime: '08:00',
    contractorName: 'Highway Builders Inc',
    contractorId: 'contractor2',
    requestedDate: '2024-01-16',
    urgency: 'medium',
    estimatedHours: 6,
    comments: 'Weather dependent - check conditions',
    status: 'pending'
  }];
  // Sample self-generated in-progress jobs
  const selfGeneratedJobs = [{
    id: 'self-job-1',
    jobName: 'Industrial Park Infrastructure',
    jobNumber: 'IND-2024-001',
    location: 'West Valley → Material Yard',
    foreman: 'Carlos Rodriguez',
    foremanPhone: '555-987-6543',
    source: 'Self',
    assignedTrucks: '4 trucks',
    status: 'in-progress',
    startDate: '2024-01-14',
    estimatedHours: 10,
    truckType: 'dump truck 14+',
    material: 'Road Base',
    customer: 'Industrial Development Corp',
    loadLocation: 'West Valley Materials Yard',
    dumpLocation: 'Industrial Park Site',
    startTime: '07:30',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-662">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-663">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '60%'
        }} data-id="element-664"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-665">120/200 tons (60%)</span>
        </div>
  }, {
    id: 'self-job-2',
    jobName: 'Downtown Office Complex',
    jobNumber: 'DOC-2024-005',
    location: 'Downtown SLC → Quarry East',
    foreman: 'Jessica Miller',
    foremanPhone: '555-456-7890',
    source: 'Self',
    assignedTrucks: '3 trucks',
    status: 'in-progress',
    startDate: '2024-01-15',
    estimatedHours: 8,
    truckType: 'strong arm 12',
    material: 'Gravel',
    customer: 'City Center Developers',
    loadLocation: 'Quarry East',
    dumpLocation: 'Downtown SLC Office Complex',
    startTime: '08:00',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-666">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-667">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '45%'
        }} data-id="element-668"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-669">90/200 tons (45%)</span>
        </div>
  }, {
    id: 'self-job-3',
    jobName: 'North Valley Commercial Plaza',
    jobNumber: 'NVC-2024-008',
    location: 'North Valley → Sand Pit',
    foreman: 'Mark Wilson',
    foremanPhone: '555-789-0123',
    source: 'Self',
    assignedTrucks: '5 trucks',
    status: 'in-progress',
    startDate: '2024-01-13',
    estimatedHours: 12,
    truckType: 'belly dump',
    material: 'Sand',
    customer: 'North Valley Enterprises',
    loadLocation: 'Sand Pit North',
    dumpLocation: 'North Valley Commercial Plaza',
    startTime: '06:30',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-670">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-671">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '75%'
        }} data-id="element-672"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-673">300/400 tons (75%)</span>
        </div>
  }];
  // Sample contractor-assigned in-progress jobs
  const contractorAssignedJobs = [{
    id: 'contractor-job-1',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    location: 'Downtown SLC → Granite Quarry',
    foreman: 'John Smith',
    foremanPhone: '555-123-4567',
    contractor: 'ABC Construction',
    contractorId: 'contractor1',
    assignedTrucks: '3 trucks',
    status: 'in-progress',
    startDate: '2024-01-15',
    estimatedHours: 8,
    truckType: 'dump truck 12',
    material: 'Gravel',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-674">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-675">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '65%'
        }} data-id="element-676"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-677">195/300 tons (65%)</span>
        </div>
  }, {
    id: 'contractor-job-2',
    jobName: 'Highway 89 Expansion Project',
    jobNumber: 'HWY-2024-002',
    location: 'North Valley → Sand Pit',
    foreman: 'Mike Johnson',
    foremanPhone: '555-234-5678',
    contractor: 'Highway Builders Inc',
    contractorId: 'contractor2',
    assignedTrucks: '2 trucks',
    status: 'in-progress',
    startDate: '2024-01-16',
    estimatedHours: 6,
    truckType: 'strong arm 14',
    material: 'Sand',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-678">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-679">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '40%'
        }} data-id="element-680"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-681">80/200 tons (40%)</span>
        </div>
  }, {
    id: 'contractor-job-3',
    jobName: 'Residential Development Phase 2',
    jobNumber: 'RES-2024-003',
    location: 'Sugar House → Gravel Yard',
    foreman: 'Sarah Williams',
    foremanPhone: '555-345-6789',
    contractor: 'Residential Builders LLC',
    contractorId: 'contractor3',
    assignedTrucks: '4 trucks',
    status: 'in-progress',
    startDate: '2024-01-14',
    estimatedHours: 10,
    truckType: 'dump truck 14+',
    material: 'Topsoil',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-682">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-683">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '85%'
        }} data-id="element-684"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-685">127/150 tons (85%)</span>
        </div>
  }];
  // Sample in-progress jobs
  const inProgressJobs = [{
    id: '1',
    jobName: 'Residential Development Phase 2',
    jobNumber: 'RES-2024-003',
    location: 'Sugar House → Gravel Yard',
    foreman: 'Sarah Williams',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-686">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-687">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '60%'
        }} data-id="element-688"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-689">120/200 tons (60%)</span>
        </div>
  }];
  // Sample completed jobs
  const completedJobs = [{
    id: 'c1',
    name: 'Industrial Park Infrastructure',
    jobNumber: 'IND-2024-001',
    date: '2024-01-10',
    broker: 'Self',
    truckingCompany: 'Mountain View Trucking',
    driver: 'Tom Anderson',
    truckType: 'Dump Truck',
    material: 'Concrete',
    totalTons: '145.00',
    rate: '$90/ton',
    amount: '$1,305.00'
  }];
  const tabs = [{
    id: 'jobs',
    label: 'Jobs Overview',
    icon: <FileTextIcon size={18} data-id="element-690" />
  }, {
    id: 'available-trucks',
    label: 'Available Trucks',
    icon: <TruckIcon size={18} data-id="element-691" />,
    badge: availableTrucks.filter(truck => truck.availability[getDateString(1)] === true && truck.status === 'available').length
  }, {
    id: 'contractor-requests',
    label: 'Contractor Requests',
    icon: <AlertCircleIcon size={18} data-id="element-692" />,
    badge: contractorJobRequests.length
  }, {
    id: 'assign',
    label: 'Assign to Trucking Companies',
    icon: <CalendarDaysIcon size={18} data-id="element-693" />
  }];
  // Handle creating a similar job from an existing self-generated job
  const handleCreateSimilarJob = (jobId: string) => {
    // Find the job in self-generated jobs
    const job = selfGeneratedJobs.find(j => j.id === jobId);
    if (job) {
      // Get tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowFormatted = tomorrow.toISOString().split('T')[0];
      // Create a new job number with current date
      const currentDate = new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const jobNumberBase = job.jobNumber.split('-').slice(0, -1).join('-');
      const newJobNumber = `${jobNumberBase}-${month}${day}`;
      // Create prefilled data for the new job
      const prefilledData = {
        jobName: `${job.jobName} - Next Day`,
        jobNumber: newJobNumber,
        date: tomorrowFormatted,
        startTime: job.startTime || '08:00',
        material: job.material,
        numberOfTrucks: job.assignedTrucks,
        truckType: job.truckType,
        loadLocation: job.loadLocation,
        dumpLocation: job.dumpLocation,
        foreman: job.foreman,
        foremanPhone: job.foremanPhone,
        customer: job.customer,
        estimatedHours: job.estimatedHours,
        rateType: 'hourly',
        comments: `Recreated from job ${job.jobNumber}`,
        geoPunchRequired: true,
        geoPunchDistance: 150
      };
      // Set the prefilled data and show the create job form
      setPrefilledJobData(prefilledData);
      setShowCreateJobForm(true);
    }
  };
  const handleCreateJob = (jobData: any) => {
    console.log('Broker job created:', jobData);
    const newJobId = `broker-job-${Date.now()}`;
    const newJob: Job = {
      id: newJobId,
      jobName: jobData.jobName,
      jobNumber: jobData.jobNumber,
      date: jobData.date,
      startTime: jobData.startTime,
      material: jobData.material,
      numberOfTrucks: jobData.numberOfTrucks,
      loadLocation: jobData.loadLocation,
      dumpLocation: jobData.dumpLocation,
      foreman: jobData.foreman,
      truckType: jobData.truckType,
      travelTime: jobData.travelTime,
      customer: jobData.customer,
      source: 'broker',
      status: 'pending'
    };
    setPendingJobs(prev => [...prev, newJob]);
    setShowCreateJobForm(false);
    setPrefilledJobData(null);
    setActiveTab('assign');
  };
  const handleAcceptContractorJob = (contractorJob: any) => {
    const newJob: Job = {
      id: contractorJob.id,
      jobName: contractorJob.jobName,
      jobNumber: contractorJob.jobNumber,
      date: contractorJob.date,
      startTime: contractorJob.startTime,
      material: contractorJob.material,
      numberOfTrucks: contractorJob.numberOfTrucks,
      loadLocation: contractorJob.loadLocation,
      dumpLocation: contractorJob.dumpLocation,
      foreman: contractorJob.foreman,
      truckType: contractorJob.truckType,
      travelTime: contractorJob.travelTime,
      source: 'contractor',
      contractorId: contractorJob.contractorId,
      contractorName: contractorJob.contractorName,
      status: 'pending'
    };
    setPendingJobs(prev => [...prev, newJob]);
    setActiveTab('assign');
  };
  const handleAssignJobs = (jobId: string, assignments: {
    truckingCompanyId: string;
    truckCount: number;
  }[]) => {
    console.log(`Job ${jobId} assigned to trucking companies:`, assignments);
    setPendingJobs(prev => prev.map(job => job.id === jobId ? {
      ...job,
      assignments,
      status: 'assigned'
    } : job));
    alert('Job successfully assigned to trucking companies');
  };
  // If showing create job form, render it instead of the dashboard
  if (showCreateJobForm) {
    return <div className="p-6 w-full" data-id="element-694">
        <CreateJobForm onCancel={() => {
        setShowCreateJobForm(false);
        setPrefilledJobData(null);
      }} onSubmit={handleCreateJob} prefilledData={prefilledJobData} userRole="broker" data-id="element-695" />
      </div>;
  }
  return <div className="p-6 w-full" data-id="element-696">
      <h1 className="text-2xl font-bold mb-6 text-center" data-id="element-697">Broker Dashboard</h1>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-698">
        <ActionCard icon={<PlusIcon size={20} data-id="element-700" />} title="Create New Job" onClick={() => setShowCreateJobForm(true)} color="bg-blue-500 hover:bg-blue-600" data-id="element-699" />
        <ActionCard icon={<TruckIcon size={20} data-id="element-702" />} title="View Available Trucks" onClick={() => setActiveTab('available-trucks')} color="bg-green-500 hover:bg-green-600" subAction={{
        label: `${availableTrucks.filter(truck => truck.availability[getDateString(1)] === true && truck.status === 'available').length} available tomorrow`,
        onClick: () => setActiveTab('available-trucks')
      }} data-id="element-701" />
        <ActionCard icon={<FileUpIcon size={20} data-id="element-704" />} title="Import Jobs" onClick={() => setShowImportModal(true)} color="bg-orange-500 hover:bg-orange-600" subAction={{
        label: 'View Import Format',
        onClick: () => setShowImportModal(true)
      }} data-id="element-703" />
        <ActionCard icon={<HandshakeIcon size={20} data-id="element-706" />} title="Manage Trucking Companies" onClick={() => onNavigate ? onNavigate('manage-trucking') : null} color="bg-purple-500 hover:bg-purple-600" data-id="element-705" />
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-707" />

      {/* Tab Content */}
      <div data-id="element-708">
        {activeTab === 'jobs' && <>
            {/* Self-Generated Jobs Section */}
            <SelfGeneratedJobs jobs={selfGeneratedJobs} onViewDetails={jobId => alert(`View details for job ${jobId}`)} onMessageForeman={jobId => alert(`Message foreman for job ${jobId}`)} onCreateSimilarJob={handleCreateSimilarJob} data-id="element-709" />
            {/* Contractor-Assigned Jobs Section */}
            <BrokerInProgressJobs jobs={contractorAssignedJobs} onViewDetails={jobId => alert(`View details for job ${jobId}`)} onMessageForeman={jobId => alert(`Message foreman for job ${jobId}`)} onMessageContractor={(jobId, contractorId) => alert(`Message contractor ${contractorId} about job ${jobId}`)} onUpdateStatus={jobId => alert(`Update status for job ${jobId}`)} data-id="element-710" />
            <CompletedJobs jobs={completedJobs} data-id="element-711" />
          </>}
        {activeTab === 'available-trucks' && <AvailableTrucks availableTrucks={availableTrucks} truckingCompanies={truckingCompanies} onAssignTruckToJob={(truckId, jobId) => alert(`Assign truck ${truckId} to job ${jobId}`)} onContactDriver={truckId => alert(`Contact driver for truck ${truckId}`)} onRequestTruck={(truckId, date) => alert(`Request truck ${truckId} for ${date}`)} data-id="element-712" />}
        {activeTab === 'contractor-requests' && <ContractorJobRequests requests={contractorJobRequests} onAcceptJob={handleAcceptContractorJob} onDeclineJob={jobId => alert(`Declined job ${jobId}`)} onNegotiateJob={jobId => alert(`Negotiate terms for job ${jobId}`)} data-id="element-713" />}
        {activeTab === 'assign' && <AssignJobsToTruckingCompanies jobs={pendingJobs.filter(job => job.status !== 'assigned')} truckingCompanies={truckingCompanies} onAssignJobs={handleAssignJobs} data-id="element-714" />}
      </div>

      {/* Import Jobs Modal */}
      <ImportJobsModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} data-id="element-715" />
    </div>;
};